<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0">Card Title</label>
            <input type="checkbox" v-model="fields.cardTitle.show" />
          </div>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.cardTitle.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Main Media</label>
          <div class="group-item">
            <v-radio-group v-model="fields.mainMedia.type" row hide-details>
              <v-radio label="Video" value="video" />
              <v-radio label="Image" value="image" />
            </v-radio-group>
            <div v-if="fields.mainMedia.type === 'video'" class="mt-4">
              <v-text-field v-model="fields.mainMedia.videoSrc" label="Video URL" hide-details />
            </div>
            <div v-else class="mt-4">
              <UploadImage
                @callback="handleUploadMainImage"
                :image="{ imageSrc: fields.mainMedia.imageSrc }"
                :height="`180`"
                :typeUpload="'image'"
                @callbackDelete="handleRemoveMainImage"
              />
              <v-text-field v-model="fields.mainMedia.imageAlt" class="mt-3" label="Image alt" hide-details />
            </div>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Small Items</label>
            <v-btn small text color="primary" :disabled="fields.items.length >= maxSmallItems" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Item</span>
            </v-btn>
          </div>
          <div class="caption mb-2">Maximum {{ maxSmallItems }} items.</div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.items" :key="`satellite-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Item {{ index + 1 }}</span>
                    <v-btn icon @click.stop="handleRemoveItem(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                    v-model.number="item.overlayOpacity"
                    type="number"
                    label="Overlay opacity"
                    min="0"
                    max="1"
                    step="0.1"
                    hide-details
                  />
                  <div class="mt-4">
                    <label class="mb-2 d-block">Content</label>
                    <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                  </div>
                  <div class="mt-4">
                    <label class="mb-2 d-block">Image</label>
                    <UploadImage
                      @callback="obj => handleUploadItemImage(obj, index)"
                      :image="{ imageSrc: item.imageSrc || '' }"
                      :height="`140`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveItemImage(index)"
                    />
                    <v-text-field v-model="item.imageAlt" class="mt-3" label="Image alt" hide-details />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-4">
          <ButtonPrimary :button="fields.button" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import ButtonPrimary from './common/button-primary.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultItemImage = 'https://cdn.shopify.com/s/files/1/0818/8690/4560/files/91a991e6-image-5.jpg';

export default {
  name: 'card-satellite-rivoneed-section-editor',
  components: {
    UploadImage,
    ButtonPrimary,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      maxSmallItems: 4,
      fields: {
        title: 'Card Satellite Rivoneed Section',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        cardTitle: {
          show: true,
          content:
            '<h2 style="text-align: center">One Light. Every Garage Job Done.</h2><p style="text-align: center">No dark corners. Just powerful light for every task.</p>',
        },
        mainMedia: {
          type: 'video',
          videoSrc: '',
          imageSrc: '',
          imageAlt: '',
        },
        items: [],
        button: {
          show: true,
          backgroundColor: '#ff0000',
          color: '#ffffff',
          radius: 8,
          text: "A Gift He'll Actually Use",
          link: '#',
          iconPrefix: { show: false, src: '' },
          iconSuffix: {
            show: true,
            src: 'https://cdn.skyfastimg.com/images/2026/06/11/image-6a2a87db55a093b7ad8c6903.png',
          },
          subBtn: { show: false, text: '' },
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        cardTitle: { ...this.fields.cardTitle, ...(extract.cardTitle || {}) },
        mainMedia: { ...this.fields.mainMedia, ...(extract.mainMedia || {}) },
        items: Array.isArray(extract.items) ? extract.items.slice(0, this.maxSmallItems) : this.fields.items,
        button: {
          ...this.fields.button,
          ...(extract.button || {}),
          iconPrefix: { ...this.fields.button.iconPrefix, ...((extract.button || {}).iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...((extract.button || {}).iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...((extract.button || {}).subBtn || {}) },
        },
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        items: this.fields.items.slice(0, this.maxSmallItems),
      };
      const updateHtml = updateHtmlFromData.sectionCardSatelliteRivoneed(this.sectionEditor.section.html, payload);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadMainImage(obj) {
      this.fields.mainMedia.imageSrc = obj.imageSrc || '';
    },
    handleRemoveMainImage() {
      this.fields.mainMedia.imageSrc = '';
    },
    handleAddItem() {
      if (this.fields.items.length >= this.maxSmallItems) return;
      this.fields.items.push({
        overlayOpacity: 0,
        content: '<h3>Strong Magnetic Grip</h3><p>Magnets lock on. Both hands free.</p>',
        imageSrc: defaultItemImage,
        imageAlt: '',
      });
    },
    handleRemoveItem(index) {
      this.fields.items.splice(index, 1);
    },
    handleUploadItemImage(obj, index) {
      if (!this.fields.items[index]) return;
      this.$set(this.fields.items[index], 'imageSrc', obj.imageSrc || '');
    },
    handleRemoveItemImage(index) {
      if (!this.fields.items[index]) return;
      this.$set(this.fields.items[index], 'imageSrc', '');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
